import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ContinuationReceiptEditModal } from '@/components/molecules';
import {
  useContinuationChangePrice,
  useContinuationInit,
  useContinuationList,
  useContinuationListPdf,
  useContinuationPrintReset,
} from '@/hooks/main/useContinuationList';
import { useClinicContext } from '@/hooks/main/useClinicContext';
import { useR2VisitClinicInvoiceList } from '@/hooks/main/useR2VisitClinicInvoiceList';
import {
  useR2VisitClinicInvoiceChangePrice,
  useR2VisitClinicInvoiceListPdf,
  useR2VisitClinicInvoicePrintReset,
} from '@/hooks/main/useR2VisitClinicInvoiceMutations';
import {
  fetchR2VisitClinicInvoiceEdit,
  saveR2VisitClinicInvoice,
} from '@/services/main/visitClinicInvoice';
import { useTheme } from '@/shared/providers/theme/ThemeContext';
import { SERVICE_TYPE_R2 } from '@/constants/serviceType';
import type {
  ContinuationListRow,
  ContinuationOptionItem,
} from '@/services/main/continuation/continuationService';
import useAppToast from '@/hooks/useAppToast';
import useAppModal from '@/hooks/useAppModal';
import { useVisitClinicInvoicePrintMutation } from '@/hooks/main/useVisitClinicInvoicePrintMutation';
import type {
  PrintType,
  VisitClinicInvoicePrintPayload,
} from '@/services/main/visitClinicInvoice/types';

import type { PrintOptionsState, SearchState, SearchTermType } from './types';
import { normalizeSearchTextFields, r2SummaryRows, toNumber } from './utils';
import {
  DEFAULT_INI_OPTIONS,
  DEFAULT_RATE_OPTIONS,
  EMPTY_LIST,
  INITIAL_SEARCH,
  INVOICE_PAGE_SIZE,
  getInitialPrintOptions,
} from './constants';
import { useSearchInit } from './hooks/useSearchInit';
import { useTableScrollY } from './hooks/useTableScrollY';
import SearchFilters from './components/SearchFilters';
import InvoiceTable from './components/InvoiceTable';
import PrintOptionsPanel from './components/PrintOptionsPanel';
import InvoiceActionBar from './components/InvoiceActionBar';
import { useMonthlyStatementRequestForm } from '@/hooks/main/useMonthlyStatementRequestForm';
import { isoMonthToJaWareki } from '@/utils/dateConverter';
import { NO_PRINT_DATA_MESSAGE } from '@/pages/main/treatmentRecord/constants';

const VisitClinicInvoicesPage: React.FC = () => {
  const toast = useAppToast();

  // 令和8年法改正 No 3 — 月別明細書発行依頼書（別紙様式5）.
  // Blank slip: no rezept selection, no print options, nothing marked 済.
  const monthlyStatementFormMutation = useMonthlyStatementRequestForm();
  const handleMonthlyStatementForm = () => {
    monthlyStatementFormMutation.mutate(undefined, {
      onError: () => toast.error('月別明細書発行依頼書の印刷に失敗しました。'),
    });
  };
  const appModal = useAppModal();
  const navigate = useNavigate();
  const [urlSearchParams, setUrlSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { tenant, clinicNumber } = useClinicContext();
  const { serviceType } = useTheme();
  const isR2 = serviceType?.toUpperCase() === SERVICE_TYPE_R2;
  const siteType = tenant ?? '';
  const clinicNo = clinicNumber ?? '';

  const { data: initData, isLoading: initLoading } = useContinuationInit();
  // 領収金額 edit and 印刷「済」 reset both write the visit row, and the two services keep
  // visits in different tables (`visits` vs `r_visits`), so the endpoints follow the service.
  const a2ChangePriceMutation = useContinuationChangePrice();
  const r2ChangePriceMutation = useR2VisitClinicInvoiceChangePrice();
  const changePriceMutation = isR2 ? r2ChangePriceMutation : a2ChangePriceMutation;
  const a2PrintResetMutation = useContinuationPrintReset();
  const r2PrintResetMutation = useR2VisitClinicInvoicePrintReset();
  const printResetMutation = isR2 ? r2PrintResetMutation : a2PrintResetMutation;

  const [search, setSearch] = useState<SearchState>(INITIAL_SEARCH);
  const [appliedSearch, setAppliedSearch] = useState<SearchState | null>(null);
  const [searchApplied, setSearchApplied] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<number[]>([]);
  const [patientPrices, setPatientPrices] = useState<Record<number, number>>({});
  const [dailyReceiptModalRecord, setDailyReceiptModalRecord] =
    useState<ContinuationListRow | null>(null);
  const [printOptions, setPrintOptions] = useState<PrintOptionsState>(getInitialPrintOptions);
  const [currentPage, setCurrentPage] = useState(1);
  // Bumped on every 検索 so the list is always recalculated (BD §4), cache or not.
  const [searchNonce, setSearchNonce] = useState(0);

  const receiptModalPrintSettings = useMemo(
    () => ({
      showPatientNumber: printOptions.option_11,
      showDate: printOptions.option_5,
      dateValue: printOptions.option_date,
      showClinicName: printOptions.option_7,
      showClinicAddress: printOptions.option_8,
      showManagerName: printOptions.option_1,
      showManagerAddress: printOptions.option_2,
      showMassagerName: printOptions.option_3,
      showMassagerAddress: printOptions.option_4,
    }),
    [printOptions]
  );

  const param = initData?.param;

  useSearchInit({
    param: param as Record<string, unknown> | undefined,
    urlSearchParams,
    setSearch,
    setAppliedSearch,
    setSearchApplied,
    setPrintOptions,
  });

  const listParams = useMemo(() => {
    if (!searchApplied || !appliedSearch) return null;

    const criteria = appliedSearch;
    const payload: Record<string, unknown> = {
      search_term_type: criteria.search_term_type,
      sort: criteria.sort,
      page: currentPage,
      per_page: INVOICE_PAGE_SIZE,
    };

    if (criteria.search_term_type === 1) {
      payload.search_treatment = criteria.search_treatment;
    }
    if (criteria.search_term_type === 2) {
      payload.search_treatment_yearmonth = criteria.search_treatment_yearmonth;
    }
    if (criteria.search_term_type === 3) {
      payload.search_treatement_from = criteria.search_treatement_from;
      payload.search_treatement_to = criteria.search_treatement_to;
      if (criteria.patient_cost_rate !== '') {
        payload.patient_cost_rate = Number(criteria.patient_cost_rate);
      }
      if (isR2) {
        if (criteria.treatment_kind !== '') {
          payload.treatment_kind = criteria.treatment_kind;
        }
      } else {
        if (criteria.bui !== '') {
          payload.bui = Number(criteria.bui);
        }
        // 初検 filters are A2-only.
        payload.shoken1 = criteria.shoken1 ? 1 : 0;
        payload.shoken2 = criteria.shoken2 ? 1 : 0;
      }
    }

    if (criteria.search_text === 1 && criteria.search_patient_id.trim() !== '') {
      payload.search_text = 1;
      payload.search_patient_id = criteria.search_patient_id.trim();
    }
    if (criteria.search_text === 2 && criteria.search_patient_name.trim() !== '') {
      payload.search_text = 2;
      payload.search_patient_name = criteria.search_patient_name.trim();
    }
    if (criteria.search_text === 3 && criteria.ini.trim() !== '') {
      payload.search_text = 3;
      payload.ini = criteria.ini.trim();
    }

    return payload;
  }, [searchApplied, appliedSearch, currentPage, isR2]);

  // A2 reads the 継続 list; R2 has its own endpoint because that module is A2-only.
  // Both return `{ list }` in the same row shape, and the page already derives 合計 and
  // the row count from `list` when the response omits totals / pagination.
  const a2List = useContinuationList(isR2 ? null : listParams, searchNonce);
  const r2List = useR2VisitClinicInvoiceList(isR2 ? listParams : null, searchNonce);
  const listData = isR2 ? r2List.data : a2List.data;
  const listLoading = isR2 ? r2List.isLoading : a2List.isLoading;

  const rateOptions = useMemo(() => {
    const src = (listData?.ratearr ?? initData?.ratearr ?? []) as ContinuationOptionItem[];
    if (!src.length) return DEFAULT_RATE_OPTIONS;
    return [
      { value: '', label: 'すべて' },
      ...src.map(item => ({ value: String(item.id), label: item.name })),
    ];
  }, [initData?.ratearr, listData?.ratearr]);

  const iniOptions = useMemo(() => {
    const src = (listData?.iniarr ?? initData?.iniarr ?? []) as ContinuationOptionItem[];
    if (!src.length) return DEFAULT_INI_OPTIONS;
    return [
      { value: '', label: 'すべて' },
      ...src.map(item => ({ value: String(item.id), label: item.name })),
    ];
  }, [initData?.iniarr, listData?.iniarr]);

  const list = listData?.list ?? EMPTY_LIST;
  const selectedSet = useMemo(() => new Set(selectedRowKeys), [selectedRowKeys]);

  useEffect(() => {
    const next: Record<number, number> = {};
    for (const row of list) {
      next[row.id] = toNumber(row.patient_price, 0);
    }
    setPatientPrices(next);
    // Removed setSelectedRowKeys([]); to keep selection after print/update
  }, [list]);

  const freeMedicalsForModal = useMemo(
    () => listData?.free_medicals ?? initData?.free_medicals ?? [],
    [listData?.free_medicals, initData?.free_medicals]
  );

  // Server returns totals over the full result set so the summary row stays
  // correct across pages. Fall back to the visible page only when totals are
  // absent (e.g. server hasn't been redeployed yet).
  const totalMadoguchi = useMemo(
    () =>
      listData?.totals?.madoguchi ??
      list.reduce((sum, row) => sum + toNumber(row.madoguchi_price, 0), 0),
    [listData?.totals?.madoguchi, list]
  );
  const totalTreatment = useMemo(
    () =>
      listData?.totals?.treatment ??
      list.reduce((sum, row) => sum + toNumber(row.treatment_price, 0), 0),
    [listData?.totals?.treatment, list]
  );
  const totalPatient = useMemo(
    () =>
      listData?.totals?.patient ??
      list.reduce(
        (sum, row) => sum + toNumber(patientPrices[row.id], toNumber(row.patient_price, 0)),
        0
      ),
    [listData?.totals?.patient, list, patientPrices]
  );

  const updateSearch = useCallback(<K extends keyof SearchState>(key: K, value: SearchState[K]) => {
    setSearch(prev => {
      const next = { ...prev, [key]: value };
      return key === 'search_text' ? normalizeSearchTextFields(next) : next;
    });
  }, []);

  const validateSearch = useCallback(() => {
    if (search.search_term_type === 1 && !search.search_treatment) {
      toast.error('日付を入力してください');
      return false;
    }
    if (search.search_term_type === 2 && !search.search_treatment_yearmonth) {
      toast.error('日付を入力してください');
      return false;
    }
    if (
      search.search_term_type === 3 &&
      (!search.search_treatement_from || !search.search_treatement_to)
    ) {
      toast.error('日付を入力してください');
      return false;
    }
    return true;
  }, [
    search.search_term_type,
    search.search_treatment,
    search.search_treatment_yearmonth,
    search.search_treatement_from,
    search.search_treatement_to,
    toast,
  ]);

  const handleSearch = useCallback(() => {
    if (!validateSearch()) return;
    const nextQuery = new URLSearchParams();
    nextQuery.set('run', '1');
    nextQuery.set('search_term_type', String(search.search_term_type));
    nextQuery.set('sort', String(search.sort));

    if (search.search_term_type === 1 && search.search_treatment) {
      nextQuery.set('search_treatment', search.search_treatment);
    }
    if (search.search_term_type === 2 && search.search_treatment_yearmonth) {
      nextQuery.set('search_treatment_yearmonth', search.search_treatment_yearmonth);
    }
    if (search.search_term_type === 3) {
      if (search.search_treatement_from) {
        nextQuery.set('search_treatement_from', search.search_treatement_from);
      }
      if (search.search_treatement_to) {
        nextQuery.set('search_treatement_to', search.search_treatement_to);
      }
      if (search.patient_cost_rate !== '') {
        nextQuery.set('patient_cost_rate', search.patient_cost_rate);
      }
      if (search.bui !== '') {
        nextQuery.set('bui', search.bui);
      }
      nextQuery.set('shoken1', search.shoken1 ? '1' : '0');
      nextQuery.set('shoken2', search.shoken2 ? '1' : '0');
    }

    if (search.search_text !== 0) {
      nextQuery.set('search_text', String(search.search_text));
      if (search.search_text === 1 && search.search_patient_id.trim() !== '') {
        nextQuery.set('search_patient_id', search.search_patient_id.trim());
      }
      if (search.search_text === 2 && search.search_patient_name.trim() !== '') {
        nextQuery.set('search_patient_name', search.search_patient_name.trim());
      }
      if (search.search_text === 3 && search.ini !== '') {
        nextQuery.set('ini', search.ini);
      }
    }

    setUrlSearchParams(nextQuery, { replace: true });
    setAppliedSearch({ ...search });
    setSearchApplied(true);
    setCurrentPage(1);
    setSelectedRowKeys([]);
    setSearchNonce(n => n + 1);
  }, [search, setUrlSearchParams, validateSearch]);

  const handleTermTypeChange = useCallback((value: SearchTermType) => {
    setSearch(prev => ({ ...prev, search_term_type: value }));
    setPrintOptions(prev => {
      if (value === 2) {
        return {
          ...prev,
          print_visit_clinic_invoice: false,
          print_visit_clinic_invoice_100: false,
          print_visit_clinic_invoice_free_medical: false,
          print_visit_clinic_invoice_free_medical_100: false,
          print_monthly: true,
        };
      }
      return { ...prev, print_monthly: false, print_monthly_100: false };
    });
    setSelectedRowKeys([]);
  }, []);

  const handleSelectAllToggle = useCallback(() => {
    if (selectedRowKeys.length !== list.length) {
      setSelectedRowKeys(list.map(row => row.id));
      return;
    }
    setSelectedRowKeys([]);
  }, [selectedRowKeys.length, list]);

  const handleSelectInvert = useCallback(() => {
    const selected = new Set(selectedRowKeys);
    setSelectedRowKeys(list.filter(row => !selected.has(row.id)).map(row => row.id));
  }, [selectedRowKeys, list]);

  const handleToggleRow = useCallback(
    (id: number, checked: boolean) => {
      const isMonthly = search.search_term_type === 2;
      const target = list.find(row => row.id === id);
      const siblingIds =
        isMonthly && target
          ? list.filter(row => row.patient_id === target.patient_id).map(row => row.id)
          : [id];

      setSelectedRowKeys(prev => {
        const next = new Set(prev);
        if (checked) {
          siblingIds.forEach(sid => next.add(sid));
        } else {
          siblingIds.forEach(sid => next.delete(sid));
        }
        return Array.from(next);
      });
    },
    [search.search_term_type, list]
  );

  const handleChangePatientPrice = useCallback((visitId: number, value: number | null) => {
    setPatientPrices(prev => ({
      ...prev,
      [visitId]: value == null ? 0 : Math.max(0, Number(value)),
    }));
  }, []);

  const handleBlurPatientPrice = useCallback(
    (record: ContinuationListRow) => {
      const nextValue = toNumber(patientPrices[record.id], toNumber(record.patient_price, 0));
      const madoguchi = toNumber(record.madoguchi_price, 0);
      const diff = nextValue - madoguchi;

      if (diff < 0) {
        toast.info('窓口料金より低い領収金額を設定することはできません。');
        setPatientPrices(prev => ({ ...prev, [record.id]: madoguchi }));
        return;
      }

      changePriceMutation.mutate({ visitId: record.id, freeMedicalFee: diff });
    },
    [changePriceMutation, patientPrices, toast]
  );

  const handlePrintReset = useCallback(
    (visitId: number) => {
      appModal.confirm({
        title: '印刷済みをリセットしますか？',
        okText: 'OK',
        cancelText: 'キャンセル',
        onOk: () => printResetMutation.mutate(visitId),
      });
    },
    [appModal, printResetMutation]
  );

  const hasAnyPrintTarget =
    printOptions.print_visit_clinic_invoice ||
    printOptions.print_visit_clinic_invoice_free_medical ||
    printOptions.print_monthly;

  const { triggerPrint, isLoading: isPrinting } = useVisitClinicInvoicePrintMutation();
  // 一覧印刷 reads the list from the DB again, so it has to go to the endpoint that
  // queries the right table: A2's continuation one reads `visits` and finds nothing for R2.
  const { mutateAsync: a2ListPdfMutation, isPending: isA2ListPrinting } = useContinuationListPdf();
  const { mutateAsync: r2ListPdfMutation, isPending: isR2ListPrinting } =
    useR2VisitClinicInvoiceListPdf();
  const isListPrinting = isR2 ? isR2ListPrinting : isA2ListPrinting;

  const buildPrintPayload = useCallback(
    (printType: PrintType): VisitClinicInvoicePrintPayload => {
      const actualType: PrintType =
        printType === 'format' ? printType : printOptions.print_monthly ? 'monthly' : 'receipt';
      const criteria = appliedSearch;

      const payload: VisitClinicInvoicePrintPayload = {
        visit_ids: printType === 'format' ? undefined : selectedRowKeys.map(Number),
        print_type: actualType,
        print_receipt: printOptions.print_visit_clinic_invoice,
        print_free_medical: printOptions.print_visit_clinic_invoice_free_medical,
        print_monthly: printOptions.print_monthly,
        print_receipt_100: printOptions.print_visit_clinic_invoice_100,
        print_free_medical_100: printOptions.print_visit_clinic_invoice_free_medical_100,
        print_monthly_100: printOptions.print_monthly_100,
        is_clinic_name: printType === 'format' ? false : printOptions.option_7,
        is_clinic_address: printType === 'format' ? false : printOptions.option_8,
        is_manager_name: printType === 'format' ? false : printOptions.option_1,
        is_manager_address: printType === 'format' ? false : printOptions.option_2,
        is_massager_name: printType === 'format' ? false : printOptions.option_3,
        is_massager_address: printType === 'format' ? false : printOptions.option_4,
        is_patient_number: printType === 'format' ? false : printOptions.option_11,
        is_date: printOptions.option_5,
        print_date: printType === 'format' ? undefined : printOptions.option_date || undefined,
      };

      if (criteria) {
        payload.search_term_type = criteria.search_term_type;
        payload.sort = criteria.sort;
        payload.search_treatment = criteria.search_treatment;
        payload.search_treatment_yearmonth = criteria.search_treatment_yearmonth;
        payload.search_treatement_from = criteria.search_treatement_from;
        payload.search_treatement_to = criteria.search_treatement_to;
        payload.patient_cost_rate =
          criteria.patient_cost_rate !== '' ? Number(criteria.patient_cost_rate) : undefined;
        payload.bui = criteria.bui !== '' ? Number(criteria.bui) : undefined;
        payload.shoken1 = criteria.shoken1 ? 1 : 0;
        payload.shoken2 = criteria.shoken2 ? 1 : 0;
        payload.search_text = criteria.search_text !== 0 ? criteria.search_text : undefined;
        payload.search_patient_id = criteria.search_patient_id;
        payload.search_patient_name = criteria.search_patient_name;
        payload.ini = criteria.ini;
      }

      return payload;
    },
    [printOptions, selectedRowKeys, appliedSearch]
  );

  const runPrint = useCallback(
    async (printType: PrintType) => {
      if (printType !== 'format' && selectedRowKeys.length === 0) {
        appModal.error({ title: '受診者を選択してください。' });
        return;
      }
      if (!hasAnyPrintTarget) {
        appModal.error({ title: '出力帳票を選択してください。' });
        return;
      }
      await triggerPrint(buildPrintPayload(printType));
    },
    [selectedRowKeys, hasAnyPrintTarget, triggerPrint, buildPrintPayload, appModal]
  );

  /**
   * SCR-183 §16/§17 — 印刷 / 保険外明細付 inside the 領収明細編集 modal, for R2.
   *
   * One visit, receipt page always, 保険外明細 page on demand. The print options the desk
   * set on the list travel with it, and the backend persists 印刷済み = ④支払金額 from the
   * same total it prints, so 「済」 and ③領収金額 land on the value the modal showed.
   */
  const handleModalPrint = useCallback(
    async (visitId: number, withFreeMedical: boolean) => {
      const payload = buildPrintPayload('receipt');
      await triggerPrint({
        ...payload,
        visit_ids: [visitId],
        print_type: 'receipt',
        print_receipt: true,
        print_free_medical: withFreeMedical,
        print_monthly: false,
      }, { rethrow: true });
    },
    [buildPrintPayload, triggerPrint]
  );

  const handleBulkPrint = useCallback(() => runPrint('receipt'), [runPrint]);
  const handleFormatPrint = useCallback(() => runPrint('format'), [runPrint]);

  // 一覧印刷 hits the dedicated continuation list-PDF endpoint, not the
  // per-receipt one used by 印刷 / 雛形印刷. It always prints the full
  // search result — the backend rebuilds the list from the search params,
  // so we intentionally omit visit_ids regardless of pagination/selection.
  const handleListPrint = useCallback(async () => {
    if (list.length === 0) {
      toast.error(NO_PRINT_DATA_MESSAGE);
      return;
    }
    if (!appliedSearch) {
      toast.error('検索条件を指定してください。');
      return;
    }

    const termType = Number(appliedSearch.search_term_type ?? 1);
    const printReceipt100 =
      termType === 2
        ? printOptions.print_monthly_100
        : printOptions.print_visit_clinic_invoice_100 ||
          printOptions.print_visit_clinic_invoice_free_medical_100;
    const requestPayload: Parameters<typeof a2ListPdfMutation>[0] = {
      search_term_type: termType,
      sort: Number(appliedSearch.sort ?? 0),
      print_receipt_100: printReceipt100 ? 1 : 0,
      search_text: appliedSearch.search_text,
      search_patient_id: appliedSearch.search_patient_id,
      search_patient_name: appliedSearch.search_patient_name,
      ini: appliedSearch.ini,
    };

    if (termType === 1) {
      requestPayload.search_treatment = appliedSearch.search_treatment ?? '';
    } else if (termType === 2) {
      requestPayload.search_treatment_yearmonth = appliedSearch.search_treatment_yearmonth ?? '';
    } else if (termType === 3) {
      requestPayload.search_treatement_from = appliedSearch.search_treatement_from ?? '';
      requestPayload.search_treatement_to = appliedSearch.search_treatement_to ?? '';
      if (appliedSearch.patient_cost_rate !== '') {
        requestPayload.patient_cost_rate = Number(appliedSearch.patient_cost_rate);
      }
      if (appliedSearch.bui !== '') {
        requestPayload.bui = Number(appliedSearch.bui);
      }
      requestPayload.shoken1 = appliedSearch.shoken1 ? 1 : 0;
      requestPayload.shoken2 = appliedSearch.shoken2 ? 1 : 0;
    }

    const tab = window.open('', '_blank');
    try {
      const blob = isR2
        ? await r2ListPdfMutation(requestPayload)
        : await a2ListPdfMutation(requestPayload);
      const url = URL.createObjectURL(blob);
      if (tab) {
        tab.location.href = url;
      } else {
        window.open(url, '_blank');
      }
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      tab?.close();
      toast.error('印刷に失敗しました。');
    }
  }, [list, appliedSearch, printOptions, isR2, a2ListPdfMutation, r2ListPdfMutation, toast]);

  const { tableWrapperRef } = useTableScrollY();

  const isLoading = initLoading || (searchApplied && listLoading);
  const termType = search.search_term_type;
  const printTargetsDisabled = termType === 2;
  const monthlyDisabled = termType !== 2;

  const handleNavigateTreatment = useCallback(
    (patientId: number | string, yearmonth: string) => {
      const jaYm = isoMonthToJaWareki(yearmonth) || yearmonth;
      navigate(`../visit_clinic/edit/${patientId}/${jaYm}`);
    },
    [navigate]
  );

  return (
    <div className="pb-4">
      <SearchFilters
        isR2={isR2}
        search={search}
        termType={termType}
        rateOptions={rateOptions}
        iniOptions={iniOptions}
        updateSearch={updateSearch}
        setSearch={setSearch}
        onTermTypeChange={handleTermTypeChange}
        onSearch={handleSearch}
      />

      {searchApplied ? (
        <>
          <div className="flex flex-col flex-1 min-h-0">
            <InvoiceTable
              isR2={isR2}
              list={list}
              isLoading={isLoading}
              selectedSet={selectedSet}
              patientPrices={patientPrices}
              tableWrapperRef={tableWrapperRef}
              totalMadoguchi={totalMadoguchi}
              totalTreatment={totalTreatment}
              totalPatient={totalPatient}
              currentPage={currentPage}
              pageSize={INVOICE_PAGE_SIZE}
              totalCount={listData?.pagination?.total ?? list.length}
              onPageChange={setCurrentPage}
              onNavigateTreatment={handleNavigateTreatment}
              onSelectAllToggle={handleSelectAllToggle}
              onSelectInvert={handleSelectInvert}
              onToggleRow={handleToggleRow}
              onChangePatientPrice={handleChangePatientPrice}
              onBlurPatientPrice={handleBlurPatientPrice}
              onPrintReset={handlePrintReset}
              onOpenDailyReceipt={setDailyReceiptModalRecord}
            />
          </div>

          <div className="shrink-0">
            <PrintOptionsPanel
              isR2={isR2}
              printOptions={printOptions}
              setPrintOptions={setPrintOptions}
              printTargetsDisabled={printTargetsDisabled}
              monthlyDisabled={monthlyDisabled}
            />
          </div>

          <InvoiceActionBar
            onBulkPrint={handleBulkPrint}
            onFormatPrint={handleFormatPrint}
            onListPrint={handleListPrint}
            onMonthlyStatementForm={handleMonthlyStatementForm}
            isPrinting={isPrinting || isListPrinting}
            isMonthlyStatementFormPrinting={monthlyStatementFormMutation.isPending}
          />
        </>
      ) : null}

      <ContinuationReceiptEditModal
        isOpen={!!dailyReceiptModalRecord}
        onClose={() => setDailyReceiptModalRecord(null)}
        record={dailyReceiptModalRecord}
        freeMedicals={freeMedicalsForModal}
        siteType={siteType}
        clinicNumber={clinicNo}
        onSaved={() =>
          queryClient.invalidateQueries({
            queryKey: [isR2 ? 'visitClinicInvoice' : 'continuation'],
          })
        }
        printSettings={receiptModalPrintSettings}
        fetchEdit={isR2 ? fetchR2VisitClinicInvoiceEdit : undefined}
        save={isR2 ? saveR2VisitClinicInvoice : undefined}
        summaryRows={isR2 ? r2SummaryRows : undefined}
        printReceipt={isR2 ? handleModalPrint : undefined}
      />
    </div>
  );
};

export default VisitClinicInvoicesPage;
